import { Paper, Stack, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import React from "react";
import { format } from "date-fns";
import ModuleHeader from "../ModuleHeader";
import { CalendarDots } from "@phosphor-icons/react";
import EmptyImage from "assets/bcba_dashboar_empty_section.png";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import ANChartJSWrapper from "../../../elements/ANChartJSWrapper";
import ANLoadingScreen from "../../../elements/ANLoadingScreen";
import CustomChartTooltip from "./CustomChartTooltip";
import useClinicianGetHours from "hooks/api/clinician/useClinicianGetHours";
import * as selectors from "selectors";
import { useSelector } from "react-redux";
import {
  FILTERS,
  getStartDateAndEndDate,
  authSchedDeliveredColors,
  generateDynamicLabels,
  getLastWeekDeliveredPorcent,
  isHoursEmpty,
} from "../utils";
import { ModuleContainer } from "../styles";
import ClientSelect from "../ClientSelect";
/** @typedef {import("api/typesDef").Client} Client */

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

/**
 * @component
 * @description `AuthSchedDelivered` component to display authorized, scheduled, and delivered hours by client and time unit (weeks or months) in a bar chart.
 * @param {Object} props
 * @param {Client[]} props.clients - The list of clients assigned to the clinician
 * @param {boolean} props.isSmallScreen - Whether the screen is small
 * @returns {JSX.Element} AuthSchedDelivered component
 */
export default function AuthSchedDelivered({ clients, isSmallScreen }) {
  const [timeUnit, setTimeUnit] = React.useState(FILTERS.BY_WEEKS);
  const [client, setClient] = React.useState("all");
  const [chartContext, setChartContext] = React.useState(null);
  const [period, setPeriod] = React.useState("1 week");

  // Using utils function to get start and end dates
  const dateRange = React.useMemo(() => {
    return getStartDateAndEndDate(timeUnit);
  }, [timeUnit]);

  const [startDate, setStartDate] = React.useState(dateRange.startDate);
  const { clinician_id } = useSelector(selectors.getUser);

  const hoursQuery = React.useMemo(() => {
    const baseQuery = {
      authorized: true,
      scheduled: true,
      delivered: true,
      startDate: format(startDate, "MM/dd/yyyy"),
      endDate: format(new Date(), "MM/dd/yyyy"),
      period,
    };

    if (client && client !== "all") {
      baseQuery.clientId = client.client_id;
    }

    return baseQuery;
  }, [startDate, period, client]);

  const { hours, isHoursLoading } = useClinicianGetHours(clinician_id, hoursQuery);

  const imageSize = isSmallScreen ? 168 : 223;

  const handleTimeUnitChange = React.useCallback((_, newValue) => {
    if (newValue !== null) setTimeUnit(newValue);
  }, []);

  const handleWeeksClick = React.useCallback(() => {
    setPeriod("1 week");
    const { startDate: weekStartDate } = getStartDateAndEndDate(FILTERS.BY_WEEKS);
    setStartDate(weekStartDate);
  }, []);

  const handleMonthsClick = React.useCallback(() => {
    setPeriod("1 month");
    const { startDate: monthStartDate } = getStartDateAndEndDate(FILTERS.BY_MONTHS);
    setStartDate(monthStartDate);
  }, []);

  const handleClientChange = React.useCallback((selectedClient) => {
    setClient(selectedClient);
  }, []);

  const isEmpty = isHoursEmpty(hours, isHoursLoading);

  const chartData = React.useMemo(() => {
    if (!hours) {
      return {
        labels: [],
        datasets: [
          {
            label: "Authorized hours",
            data: [],
            backgroundColor: authSchedDeliveredColors.authorized,
          },
          {
            label: "Scheduled hours",
            data: [],
            backgroundColor: authSchedDeliveredColors.scheduled,
          },
          {
            label: "Delivered hours",
            data: [],
            backgroundColor: authSchedDeliveredColors.delivered,
          },
        ],
      };
    }

    // Generate dynamic labels with consistent year formatting
    const dataLabels = generateDynamicLabels(hours.scheduled, timeUnit, isSmallScreen);

    // Extract data for each dataset
    let authorizedData =
      hours.authorized?.map((item) => parseFloat(item.total_duration) || 0) || [];
    const scheduledData =
      hours.scheduled?.map((item) => parseFloat(item.total_duration) || 0) || [];
    let deliveredData = hours.delivered?.map((item) => parseFloat(item.total_duration) || 0) || [];

    return {
      labels: dataLabels,
      datasets: [
        {
          label: "Authorized hours",
          data: authorizedData,
          backgroundColor: authSchedDeliveredColors.authorized,
          categoryPercentage: isSmallScreen ? 0.9 : 0.6,
          barPercentage: 0.85,
        },
        {
          label: "Scheduled hours",
          data: scheduledData,
          backgroundColor: authSchedDeliveredColors.scheduled,
          categoryPercentage: isSmallScreen ? 0.9 : 0.6,
          barPercentage: 0.85,
        },
        {
          label: "Delivered hours",
          data: deliveredData,
          backgroundColor: authSchedDeliveredColors.delivered,
          categoryPercentage: isSmallScreen ? 0.9 : 0.6,
          barPercentage: 0.85,
        },
      ],
    };
  }, [hours, timeUnit]);

  // Memoize the options object to prevent Chart re-initialization
  const options = React.useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: { enabled: false, external: (context) => setChartContext(context) },
      },
      scales: {
        y: { grid: { display: false }, border: { display: false } },
        x: {
          grid: { display: false },
          border: { display: false },
          ticks: {
            font: {
              size: isSmallScreen ? 10 : 14,
            },
            maxRotation: 0,
            minRotation: 0,
          },
        },
      },
      datasets: {
        bar: {
          borderWidth: 0,
          borderSkipped: false,
          borderRadius: 4,
          minBarLength: 6,
          maxBarThickness: 17,
        },
      },
    }),
    [setChartContext, isSmallScreen]
  );

  // Authorized hours / delivered hours -> percentage for the last week
  const lastWeekDeliveredPorcent = getLastWeekDeliveredPorcent(hours);

  const getDescriptionText = React.useMemo(() => {
    if (client === "all" || !client) {
      return (
        <>
          Your clients received{" "}
          <Typography
            component="span"
            variant="subtitle2"
            sx={{ fontWeight: "500", color: "primary.main" }}
          >
            {lastWeekDeliveredPorcent}%
          </Typography>{" "}
          of their authorized hours last week.
        </>
      );
    } else {
      const clientName = client.name || "Client";
      return (
        <>
          You and {clientName} reached{" "}
          <Typography
            component="span"
            variant="subtitle2"
            sx={{ fontWeight: "500", color: "primary.main" }}
          >
            {lastWeekDeliveredPorcent}%
          </Typography>{" "}
          of authorized hours last week.
        </>
      );
    }
  }, [client, lastWeekDeliveredPorcent]);

  if (isHoursLoading) {
    return (
      <Paper
        component="section"
        elevation={2}
        sx={ModuleContainer}
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          justifyContent: "center",
        }}
      >
        <ANLoadingScreen height="100%" />
      </Paper>
    );
  }

  return (
    <Paper elevation={2} sx={{ p: 10, borderRadius: "12px" }}>
      <Stack gap={7}>
        <Stack
          direction={isSmallScreen ? "column" : "row"}
          justifyContent="space-between"
          alignItems="center"
          gap={2}
        >
          <ModuleHeader
            Icon={CalendarDots}
            title="Authorized vs. Scheduled vs. Delivered"
            subtitle={isEmpty ? null : getDescriptionText}
          />

          <Stack direction="row" gap={3} sx={{ width: isSmallScreen ? "100%" : "auto" }}>
            {!isSmallScreen && (
              <ClientSelect
                clients={clients}
                selectedClient={client}
                onClientChange={handleClientChange}
                sx={{ width: 124 }}
              />
            )}
            <ToggleButtonGroup
              value={timeUnit}
              size="small"
              exclusive
              onChange={handleTimeUnitChange}
              aria-label="text alignment"
              sx={{
                width: isSmallScreen ? "100%" : "auto",
                marginBottom: isSmallScreen ? 3 : "auto",
                marginTop: isSmallScreen ? 2 : "auto",
              }}
            >
              <ToggleButton
                value={FILTERS.BY_WEEKS}
                aria-label="label"
                onClick={handleWeeksClick}
                sx={{ width: isSmallScreen ? "100%" : "auto" }}
              >
                Weeks
              </ToggleButton>
              <ToggleButton
                value={FILTERS.BY_MONTHS}
                aria-label="label"
                onClick={handleMonthsClick}
                sx={{ width: isSmallScreen ? "100%" : "auto" }}
              >
                Months
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        </Stack>

        {isEmpty && (
          <Stack alignItems="center" textAlign="center" my={5}>
            <img
              src={EmptyImage}
              height={imageSize}
              alt="Empty next appointment"
              style={{ aspectRatio: "1 / 1", objectFit: "contain", width: "auto" }}
            />
            <Typography variant="subtitle1">
              Stay in the loop as you schedule and deliver therapy hours.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              As therapy hours are scheduled, submitted, and approved, theyâ€™ll appear here.
            </Typography>
          </Stack>
        )}
        {!isEmpty && (
          <Stack sx={{ minHeight: "200px", maxHeight: isSmallScreen ? "200px" : "300px" }}>
            <ANChartJSWrapper
              chartContext={chartContext}
              customTooltipContent={
                <CustomChartTooltip chartContext={chartContext} period={period} hours={hours} />
              }
              customTooltipProps={{
                width: isSmallScreen ? "auto" : "187px",
              }}
            >
              <Bar options={options} data={chartData} />
            </ANChartJSWrapper>
          </Stack>
        )}
        {isSmallScreen && (
          <ClientSelect
            clients={clients}
            selectedClient={client}
            onClientChange={handleClientChange}
          />
        )}
      </Stack>
    </Paper>
  );
}
